
import { DatePicker as AntdDatePicker } from 'antd';
import type { DatePickerProps } from 'antd/es/date-picker';
import dayjs, { Dayjs } from 'dayjs';
import 'dayjs/locale/ru';
import cn from 'classnames';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import { useId } from 'react';

import { DatePickerFormat } from '../model/format';
import { parseDateValue } from '../lib/parseDateValue';

import styles from './AppDatePicker.module.scss';

dayjs.extend(customParseFormat);
dayjs.locale('ru');

type AppDatePickerProps = Omit<DatePickerProps, 'value' | 'format'> & {
  value?: string | Dayjs | null;
  format?: DatePickerFormat;
  mask?: boolean;
  error?: string;
  invalid?: boolean;
  suffixIconPosition?: 'left' | 'right';
};

const AppDatePicker = ({
  className,
  placeholder = 'Дата',
  value,
  onChange,
  format = DatePickerFormat.DATE,
  mask = true,
  invalid = false,
  suffixIconPosition = 'left',
  maxDate,
  disabled,
  showTime,
  suffixIcon,
  error,
  id,
  'aria-describedby': ariaDescribedBy,
  ...props
}: AppDatePickerProps) => {
  const generatedId = useId();
  const pickerId = id ?? generatedId;
  const errorId = `${pickerId}-error`;

  const parsedValue = parseDateValue(value);
  const hasError = Boolean(error);
  const isInvalid = invalid || hasError;


  return (
    <div className={styles.wrapper}>
      <AntdDatePicker
        {...props}
        id={pickerId}
        suffixIcon={suffixIcon}
        className={cn(
          className,
          styles.input,
          {
            [styles.withSuffixIcon]: suffixIcon != null,
            [styles.suffixIconRight]: suffixIconPosition === 'right',
            [styles.error]: isInvalid,
          }
        )}
        placeholder={placeholder}
        format={
          mask
            ? { format, type: 'mask' }
            : format
        }
        onChange={onChange}
        value={parsedValue}
        maxDate={maxDate}
        disabled={disabled}
        showTime={showTime}
        aria-invalid={isInvalid || undefined}
        aria-describedby={
          [
            ariaDescribedBy,
            hasError ? errorId : undefined,
          ]
            .filter(Boolean)
            .join(' ') || undefined
        }
      />

      {hasError && (
        <div
          id={errorId}
          className={styles.errorMessage}
        >
          {error}
        </div>
      )}
    </div>
  );
};

export { AppDatePicker };
